(function () {
    'use strict';

    const SVG_PREV = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" width="16" height="16"><path fill="currentColor" d="M448 256C448 264.8 440.6 272 431.4 272H54.11l140.7 149.3c6.157 6.531 5.655 16.66-1.118 22.59C190.5 446.6 186.5 448 182.5 448c-4.505 0-9.009-1.75-12.28-5.25l-165.9-176c-5.752-6.094-5.752-15.41 0-21.5l165.9-176c6.19-6.562 16.69-7 23.45-1.094c6.773 5.938 7.275 16.06 1.118 22.59L54.11 240h377.3C440.6 240 448 247.2 448 256z"/></svg>';
    const SVG_NEXT = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" width="16" height="16"><path fill="currentColor" d="M443.7 266.8l-165.9 176C274.5 446.3 269.1 448 265.5 448c-3.986 0-7.988-1.375-11.16-4.156c-6.773-5.938-7.275-16.06-1.118-22.59L393.9 272H16.59c-9.171 0-16.59-7.155-16.59-15.1S7.421 240 16.59 240h377.3l-140.7-149.3c-6.157-6.531-5.655-16.66 1.118-22.59c6.789-5.906 17.27-5.469 23.45 1.094l165.9 176C449.4 251.3 449.4 260.7 443.7 266.8z"/></svg>';
    const SVG_ELLIPSIS = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" width="24" height="32"><path fill="currentColor" d="M352 256C352 238.3 366.3 224 384 224C401.7 224 416 238.3 416 256C416 273.7 401.7 288 384 288C366.3 288 352 273.7 352 256zM192 256C192 238.3 206.3 224 224 224C241.7 224 256 238.3 256 256C256 273.7 241.7 288 224 288C206.3 288 192 273.7 192 256zM96 256C96 273.7 81.67 288 64 288C46.33 288 32 273.7 32 256C32 238.3 46.33 224 64 224C81.67 224 96 238.3 96 256z"/></svg>';

    /**
     * Generic list pagination / show-more widget.
     *
     * Usage — declarative:
     *   <ul data-paginate data-page-size="10" page-size-options="10,20,50,-1" page-size-label="Items per page">
     *
     * Usage — programmatic:
     *   const p = new QLD.ListPagination(listEl, { wrapper, pageSize, ... });
     *   p.init();
     *
     * Events dispatched on the list element (bubbles: true):
     *   pagination:init   — after first render
     *   pagination:change — after any page/size change
     *   detail: { page, pageSize, totalItems, visibleStart, visibleEnd }
     */
    class ListPagination {
        static #DEFAULTS = {
            pageSize: 10,
            pageSizeOptions: [10, 25, 50, 100, -1],
            pageSizeLabel: 'Items per page',
            allItemsLabel: 'All',
        };

        // ── State ─────────────────────────────────────────────────────────────
        #currentPage = 1;
        #selectedPageSize;
        #children = [];

        // ── Config ────────────────────────────────────────────────────────────
        #config;
        #isOrderedList;

        // ── DOM refs ──────────────────────────────────────────────────────────
        #list;
        #wrapper;   // this is the overall parent dictating where to place paginationBar data-role="pagination-wrapper"
        #paginationBar = null;
        #paginationWrapper = null;
        #itemsPerPageWrapper = null;

        /**
         * @param {HTMLElement} listElement
         * @param {object}      [options]
         * @param {HTMLElement} [options.wrapper]              Controls container (defaults to listElement.parentElement)
         * @param {number}      [options.pageSize]
         * @param {number[]}    [options.pageSizeOptions]  -1 means "All"
         * @param {string}      [options.pageSizeLabel]
         * @param {string}      [options.allItemsLabel]
         */
        constructor(listElement, options = {}) {
            const { wrapper, ...configOptions } = options;

            this.#list = listElement;
            this.#wrapper = wrapper || listElement.closest('[data-role~="pagination-wrapper"]') || listElement.parentElement;
            this.#isOrderedList = listElement instanceof HTMLOListElement;

            const dataAttrs = ListPagination.#parseDataAttributes(listElement);

            this.#config = {
                ...ListPagination.#DEFAULTS,
                ...dataAttrs,
                ...configOptions,
            };

            this.#selectedPageSize = this.#config.pageSize;
        }

        // ── Public API ────────────────────────────────────────────────────────

        /** Read list children and render. Call once after initial DOM is ready. */
        init() {
            this.#children = [...this.#list.children];
            this.#reset();
            this.#render();
            this.#dispatch('pagination:init', this.#buildEventDetail(this.#getPageSize()));
        }

        /** Re-read list children and re-render. Pass matching items when filtering. */
        refresh(items = this.#list.children) {
            this.#children = [...items];
            this.#reset();
            this.#render();
        }

        // ── Private: setup ────────────────────────────────────────────────────

        static #parseDataAttributes(element) {
            const { dataset } = element;
            const opts = {};
            if (dataset.pageSizeLabel) opts.pageSizeLabel = dataset.pageSizeLabel;
            if (dataset.allItemsLabel) opts.allItemsLabel = dataset.allItemsLabel;
            if (dataset.pageSizeOptions) {
                opts.pageSizeOptions = dataset.pageSizeOptions
                    .split(',')
                    .map(v => Number(v.trim()))
                    .filter(v => !isNaN(v));
            }
            if (dataset.pageSize) {
                opts.pageSize = Number(dataset.pageSize);
            } else if (opts.pageSizeOptions) {
                opts.pageSize = opts.pageSizeOptions[0];
            }
            return opts;
        }

        #getPageSize() {
            if (this.#selectedPageSize === -1) return this.#children.length || 1;
            return this.#selectedPageSize > 0 ? this.#selectedPageSize : this.#config.pageSize;
        }

        #reset() {
            this.#currentPage = 1;
        }

        // ── Private: render orchestration ─────────────────────────────────────

        #render() {
            if (!this.#children.length) {
                this.#removePaginationBar();
                this.#dispatch('pagination:change', this.#buildEventDetail(this.#getPageSize()));
                return;
            }

            const pageSize = this.#getPageSize();
            this.#ensureItemsPerPageControl();

            if (this.#children.length <= pageSize) {
                this.#children.forEach(card => card.classList.remove('hidden'));
                if (this.#isOrderedList) this.#list.setAttribute('start', '1');
                this.#clearPaginationControls();
                this.#ensurePaginationBar();
                this.#dispatch('pagination:change', this.#buildEventDetail(pageSize));
                return;
            }

            this.#renderPagination(pageSize);
            this.#dispatch('pagination:change', this.#buildEventDetail(pageSize));
        }

        #renderPagination(pageSize) {
            const numPages = Math.ceil(this.#children.length / pageSize);
            this.#applyPageVisibility(pageSize, numPages);

            this.#paginationWrapper?.remove();
            this.#paginationWrapper = null;
            if (numPages <= 1) return;

            this.#ensurePaginationBar();
            this.#paginationBar.insertAdjacentHTML('beforeend', this.#buildPaginationHTML(this.#currentPage, numPages));
            this.#paginationWrapper = this.#paginationBar.querySelector('[data-role~="pagination-nav"]');
        }

        #applyPageVisibility(pageSize, numPages) {
            const safePage = Math.min(Math.max(this.#currentPage, 1), numPages);
            const start = (safePage - 1) * pageSize;

            this.#currentPage = safePage;
            this.#children.forEach((card, i) => {
                card.classList.toggle('hidden', i < start || i >= start + pageSize);
            });

            if (this.#isOrderedList) this.#list.setAttribute('start', String(start + 1));
        }

        #buildPaginationHTML(current, totalPages) {
            const pageItem = (n) => {
                const active = n === current;
                return `<li class="num${active ? ' active' : ''}"><button type="button" class="qld__search-pagination_link" data-page="${n}"${active ? ' aria-current="page"' : ''}>${n}</button></li>`;
            };
            const ellipsis = `<li class="ellipsis" aria-hidden="true">${SVG_ELLIPSIS}</li>`;

            const parts = [];

            if (current > 1) {
                parts.push(`<li class="prev"><button type="button" class="qld__search-pagination_link" data-page="${current - 1}" aria-label="Previous page of results">${SVG_PREV}<span>Back</span></button></li>`);
            }

            if (totalPages <= 7) {
                for (let p = 1; p <= totalPages; p++) parts.push(pageItem(p));
            } else if (current <= 4) {
                for (let p = 1; p <= 5; p++) parts.push(pageItem(p));
                parts.push(ellipsis);
                parts.push(pageItem(totalPages));
            } else if (current >= totalPages - 3) {
                parts.push(pageItem(1));
                parts.push(ellipsis);
                for (let p = totalPages - 4; p <= totalPages; p++) parts.push(pageItem(p));
            } else {
                parts.push(pageItem(1));
                parts.push(ellipsis);
                parts.push(pageItem(current - 1));
                parts.push(pageItem(current));
                parts.push(pageItem(current + 1));
                parts.push(ellipsis);
                parts.push(pageItem(totalPages));
            }

            if (current < totalPages) {
                parts.push(`<li class="next"><button type="button" class="qld__search-pagination_link" data-page="${current + 1}" aria-label="Next page of results"><span>Next</span>${SVG_NEXT}</button></li>`);
            }

            return `<nav class="qld__no-print qld__search-pagination" aria-label="pagination" data-role="pagination-nav"><h2 class="sr-only">Pagination</h2><ul class="qld__search-pagination__list">${parts.join('')}</ul></nav>`;
        }

        // ── Private: pagination bar (shared container) ────────────────────────

        #ensurePaginationBar() {
            if (this.#paginationBar) return;

            this.#paginationBar = document.createElement('div');
            this.#paginationBar.className = 'qld__pagination-bar qld__no-print';
            this.#wrapper.appendChild(this.#paginationBar);

            this.#paginationBar.addEventListener('click', (event) => {
                const link = event.target.closest('[data-page]');
                if (!link) return;
                const clickedPageAttr = link.dataset.page;
                this.#currentPage = Number(clickedPageAttr || 1);
                const barTopBefore = this.#paginationBar.getBoundingClientRect().top;
                this.#render();
                const bar = this.#paginationBar;
                if (bar) {
                    const delta = bar.getBoundingClientRect().top - barTopBefore;
                    if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
                    const refocusTarget = bar.querySelector(`[data-page="${CSS.escape(clickedPageAttr)}"]`)
                        ?? bar.querySelector('[data-page]');
                    refocusTarget?.focus({ preventScroll: true });
                }
            });
        }

        #removePaginationBar() {
            if (!this.#paginationBar) return;
            this.#paginationBar.remove();
            this.#paginationBar = null;
            this.#itemsPerPageWrapper = null;
            this.#paginationWrapper = null;
        }

        #clearPaginationControls() {
            this.#paginationWrapper?.remove();
            this.#paginationWrapper = null;
        }

        // ── Private: items-per-page control ──────────────────────────────────

        #ensureItemsPerPageControl() {
            const minOption = Math.min(...this.#config.pageSizeOptions.filter(v => v > 0));
            const needed = this.#children.length > minOption;

            if (!needed) {
                if (this.#itemsPerPageWrapper) {
                    this.#itemsPerPageWrapper.remove();
                    this.#itemsPerPageWrapper = null;
                }
                return;
            }

            if (this.#itemsPerPageWrapper) {
                const select = this.#itemsPerPageWrapper.querySelector('[data-role~="page-size-select"]');
                if (select) select.value = String(this.#selectedPageSize);
                return;
            }
            this.#itemsPerPageWrapper = this.#createItemsPerPageControl();
            this.#ensurePaginationBar();
            this.#paginationBar.insertBefore(this.#itemsPerPageWrapper, this.#paginationBar.firstChild);
        }

        #createItemsPerPageControl() {
            const wrapper = document.createElement('div');
            wrapper.className = 'qld__page-size qld__no-print';

            const label = document.createElement('label');
            label.className = 'qld__page-size__label';

            const text = document.createElement('span');
            text.textContent = this.#config.pageSizeLabel;

            const selectWrapper = document.createElement('div');
            selectWrapper.className = 'qld__select';

            const select = document.createElement('select');
            select.className = 'qld__select-control qld__page-size__select';
            select.setAttribute('aria-label', this.#config.pageSizeLabel);
            select.dataset.role = 'page-size-select';

            this.#config.pageSizeOptions.forEach(value => {
                const option = document.createElement('option');
                option.value = String(value);
                option.textContent = value === -1 ? this.#config.allItemsLabel : String(value);
                if (value === this.#selectedPageSize) option.selected = true;
                select.appendChild(option);
            });

            select.addEventListener('change', () => {
                this.#selectedPageSize = Number(select.value);
                this.#reset();
                this.#render();
            });

            selectWrapper.appendChild(select);
            label.appendChild(text);
            label.appendChild(document.createTextNode(' '));
            label.appendChild(selectWrapper);
            wrapper.appendChild(label);
            return wrapper;
        }

        // ── Private: events ───────────────────────────────────────────────────

        #getVisibleRange(pageSize) {
            const total = this.#children.length;
            if (!total) return { start: 0, end: 0 };
            const start = (this.#currentPage - 1) * pageSize;
            return { start: start + 1, end: Math.min(start + pageSize, total) };
        }

        #buildEventDetail(pageSize) {
            const range = this.#getVisibleRange(pageSize);
            return {
                page: this.#currentPage,
                pageSize,
                totalItems: this.#children.length,
                visibleStart: range.start,
                visibleEnd: range.end,
            };
        }

        #dispatch(name, detail) {
            this.#list.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
        }
    }

    window.QLD = window.QLD || {};
    QLD.ListPagination = ListPagination;

    // Auto-initialize any list with [data-paginate]
    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('[data-paginate]').forEach(el => {
            const instance = new ListPagination(el);
            instance.init();
            el._listPagination = instance;
        });
    });
}());
